import type { PreviewTeamSettingsChangesResult } from "@redotech/admin-sdk/rpc/schema/settings-approval/preview-settings-changes";
import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { RedoSingleDatePicker } from "@redotech/arbiter/date-picker/redo-single-date-picker";
import { Flex } from "@redotech/arbiter/flex";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import { Text } from "@redotech/arbiter/text";
import { RedoToggle } from "@redotech/arbiter/toggle/redo-toggle";
import { useRequiredContext } from "@redotech/react-util/context";
import {
  normalizeStorageFees,
  storageFeesConfigSchema,
  type StorageFeesConfig,
  type StorageFeesSetting,
} from "@redotech/redo-billing-common/warehouse-storage";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import ShieldDollarIcon from "@redotech/redo-icons/shield-dollar.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { toast } from "@redotech/redo-web/alert";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { memo, useContext, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../team";
import { SettingsApprovalModal } from "../settings-approval-modal";
import { StepCard } from "./component/step-card";
import * as styles from "./warehouse-storage-fees-card.module.css";

interface WarehouseStorageFeesCardProps {
  teamId: string;
  onSaved(): void;
}
interface TierDraft {
  key: string;
  afterDays: string;
  dailyRate: string;
}
interface StorageDraft {
  enabled: boolean;
  forgivenessFrom: string;
  forgivenessThrough: string;
  tiers: TierDraft[];
  revision: number;
}

function editSetting(setting: StorageFeesSetting): StorageDraft {
  return {
    enabled: setting.config.enabled,
    forgivenessFrom: setting.config.forgivenessPeriod?.from ?? "",
    forgivenessThrough: setting.config.forgivenessPeriod?.through ?? "",
    revision: setting.revision,
    tiers: setting.config.tiers.map((tier) => ({
      key: crypto.randomUUID(),
      afterDays: String(tier.afterDays),
      dailyRate: tier.dailyRate,
    })),
  };
}
function parseDraft(
  draft: StorageDraft,
  setting: StorageFeesSetting,
):
  | { config: StorageFeesConfig; error?: never }
  | { config?: never; error: string } {
  try {
    if (!draft.enabled) {
      return {
        config: normalizeStorageFees(
          { ...setting.config, enabled: false },
          setting.currency,
        ),
      };
    }
    if (draft.tiers.length === 0) {
      return { error: "Add a fee tier before enabling storage fees" };
    }
    if (draft.tiers.some((tier) => tier.afterDays.trim() === "")) {
      return { error: "Enter a day threshold for every tier" };
    }
    if (draft.tiers.some((tier) => tier.dailyRate.trim() === "")) {
      return { error: "Enter a daily rate for every tier" };
    }
    const config = storageFeesConfigSchema.parse({
      enabled: draft.enabled,
      forgivenessPeriod:
        draft.forgivenessFrom || draft.forgivenessThrough
          ? { from: draft.forgivenessFrom, through: draft.forgivenessThrough }
          : null,
      tiers: draft.tiers.map((tier) => ({
        afterDays: Number(tier.afterDays),
        dailyRate: tier.dailyRate,
      })),
    });
    return { config: normalizeStorageFees(config, setting.currency) };
  } catch {
    return {
      error:
        "Enter both forgiveness dates in order, then use increasing whole days (0–3,650) and valid rates.",
    };
  }
}

export const WarehouseStorageFeesCard = memo(function WarehouseStorageFeesCard({
  teamId,
  onSaved,
}: WarehouseStorageFeesCardProps) {
  const rpc = useRequiredContext(RedoAdminRpcClientContext);
  const team = useContext(TeamContext);
  const args = useMemo(() => ({ teamId }), [teamId]);
  const query = useRpcQuery(
    RedoAdminRpcClientContext,
    "getManagedReturnsSettings",
    args,
  );
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<StorageDraft>();
  const [busy, setBusy] = useState(false);
  const [approval, setApproval] = useState<{
    preview: PreviewTeamSettingsChangesResult;
    config: StorageFeesConfig;
    expectedRevision: number;
  }>();
  const setting = query.data?.storageFees;
  const summary = query.data?.storageAssessmentSummary;
  const current = draft ?? (setting ? editSetting(setting) : undefined);
  const parsed = current && setting ? parseDraft(current, setting) : undefined;
  const changed = Boolean(
    draft &&
    parsed?.config &&
    setting &&
    JSON.stringify(parsed.config) !==
      JSON.stringify(normalizeStorageFees(setting.config, setting.currency)),
  );
  const change = (update: Partial<StorageDraft>) => {
    if (current) {
      setDraft({ ...current, ...update });
    }
  };
  const updateTier = (key: string, update: Partial<TierDraft>) =>
    change({
      tiers:
        current?.tiers.map((tier) =>
          tier.key === key ? { ...tier, ...update } : tier,
        ) ?? [],
    });

  const save = async () => {
    if (!parsed?.config || !current || !team) {
      return;
    }
    setBusy(true);
    try {
      const storageFees = {
        config: parsed.config,
        expectedRevision: current.revision,
      };
      const preview = await rpc.previewTeamSettingsChanges({
        teamId,
        settings: { ...team.settings },
        storageFees,
      });
      setApproval({ preview, ...storageFees });
    } catch (error) {
      toast(
        error instanceof Error
          ? error.message
          : "Could not review storage fees. Your edits are preserved.",
        { variant: "error" },
      );
    } finally {
      setBusy(false);
    }
  };
  const confirm = async (
    protectedSettingsAction: "requestApproval" | "bypassApproval",
  ) => {
    if (!approval || !team) {
      return;
    }
    setBusy(true);
    try {
      await rpc.saveManagedReturnsSettings({
        teamId,
        settings: {},
        storageFees: {
          config: approval.config,
          expectedRevision: approval.expectedRevision,
        },
        protectedSettingsAction,
      });
      setApproval(undefined);
      setDraft(undefined);
      await query.refetch();
      toast(
        protectedSettingsAction === "requestApproval"
          ? "Storage fees submitted for approval. Current pricing is unchanged."
          : "Storage fees saved. New assessments will use this pricing.",
        { variant: "success" },
      );
      onSaved();
    } catch (error) {
      toast(
        error instanceof Error
          ? error.message
          : "Could not save storage fees. Your edits are preserved.",
        { variant: "error" },
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <StepCard
        description={
          setting?.config.enabled
            ? "Daily fees after the free storage period"
            : "Not enabled"
        }
        fullWidthBody
        IconComponent={ShieldDollarIcon}
        onToggle={() => setOpen(!open)}
        open={open}
        title="Storage fees"
      >
        <Flex dir="column" gap="lg" px="2xl" py="xl">
          {query.isPending && <Text fontSize="xs">Loading storage fees…</Text>}
          {(query.isError || (!query.isPending && !setting)) && (
            <Flex dir="column" gap="sm">
              <Text fontSize="xs" textColor="error">
                Could not load storage fees.
              </Text>
              <RedoButton
                hierarchy="secondary"
                onClick={() => void query.refetch()}
                size="sm"
                text="Retry"
              />
            </Flex>
          )}
          {setting && current && (
            <>
              <RedoToggle
                description="Charge a daily fee after the free storage period."
                disabled={busy}
                label="Charge for storage"
                layout="trailing"
                settingsStyle
                setValue={(enabled) => change({ enabled })}
                size="sm"
                value={current.enabled}
              />
              {current.enabled && (
                <Flex
                  borderColor="secondary"
                  borderTopWidth="1px"
                  dir="column"
                  gap="lg"
                  pt="xl"
                >
                  <Flex dir="column" gap="sm">
                    <Text fontSize="xs" fontWeight="medium">
                      Forgiveness period for all warehouse stock
                    </Text>
                    <Text fontSize="xxs" textColor="secondary">
                      Merchant-local days in this inclusive date range will not
                      be charged. Already finalized days stay unchanged.
                    </Text>
                    <Flex align="center" gap="lg">
                      <Flex dir="column" gap="xs">
                        <Text fontSize="xxs" textColor="secondary">
                          Start date
                        </Text>
                        <RedoSingleDatePicker
                          date={
                            current.forgivenessFrom
                              ? Temporal.PlainDate.from(current.forgivenessFrom)
                              : undefined
                          }
                          disabled={busy}
                          setDate={(date) =>
                            change({ forgivenessFrom: date.toString() })
                          }
                          size="sm"
                        />
                      </Flex>
                      <Flex dir="column" gap="xs">
                        <Text fontSize="xxs" textColor="secondary">
                          End date
                        </Text>
                        <RedoSingleDatePicker
                          date={
                            current.forgivenessThrough
                              ? Temporal.PlainDate.from(
                                  current.forgivenessThrough,
                                )
                              : undefined
                          }
                          disabled={busy}
                          setDate={(date) =>
                            change({ forgivenessThrough: date.toString() })
                          }
                          size="sm"
                        />
                      </Flex>
                      {(current.forgivenessFrom ||
                        current.forgivenessThrough) && (
                        <RedoButton
                          disabled={busy}
                          hierarchy="tertiary"
                          onClick={() =>
                            change({
                              forgivenessFrom: "",
                              forgivenessThrough: "",
                            })
                          }
                          size="sm"
                          text="Clear dates"
                        />
                      )}
                    </Flex>
                  </Flex>
                  {current.tiers.length === 0 ? (
                    <Text fontSize="xs" textColor="secondary">
                      No fee tiers yet. Add one to choose when charging begins
                      and the daily rate.
                    </Text>
                  ) : (
                    <Flex align="center" gap="lg" w="full">
                      <Flex basis={0} grow={1} minW="none">
                        <Text
                          fontSize="xs"
                          fontWeight="medium"
                          textColor="secondary"
                        >
                          After this many days
                        </Text>
                      </Flex>
                      <Flex basis={0} grow={1.3} minW="none">
                        <Text
                          fontSize="xs"
                          fontWeight="medium"
                          textColor="secondary"
                        >
                          Fee per item / day
                        </Text>
                      </Flex>
                      <Flex basis="var(--redo-spacing-4xl)" shrink={0} />
                    </Flex>
                  )}
                  {current.tiers.map((tier, index) => (
                    <Flex align="center" gap="lg" key={tier.key} w="full">
                      <Flex basis={0} grow={1} minW="none">
                        <RedoTextInput
                          adornmentVariant="inline"
                          ariaLabel={`Tier ${index + 1} days`}
                          fullWidth
                          inputMode="numeric"
                          max={3650}
                          min={0}
                          setValue={(afterDays) =>
                            updateTier(tier.key, { afterDays })
                          }
                          size="sm"
                          state={busy ? "disabled" : "default"}
                          step={1}
                          suffix="days"
                          value={tier.afterDays}
                        />
                      </Flex>
                      <Flex basis={0} grow={1.3} minW="none">
                        <RedoTextInput
                          adornmentVariant="inline"
                          ariaLabel={`Tier ${index + 1} daily rate`}
                          fullWidth
                          inputMode="decimal"
                          prefix={setting.currency}
                          setValue={(dailyRate) =>
                            updateTier(tier.key, { dailyRate })
                          }
                          size="sm"
                          state={busy ? "disabled" : "default"}
                          suffix="/ item / day"
                          value={tier.dailyRate}
                        />
                      </Flex>
                      <Flex basis="var(--redo-spacing-4xl)" shrink={0}>
                        <RedoButton
                          aria-label={`Remove tier ${index + 1}`}
                          disabled={busy}
                          hierarchy="tertiary"
                          IconLeading={TrashIcon}
                          onClick={() =>
                            change({
                              tiers: current.tiers.filter(
                                (row) => row.key !== tier.key,
                              ),
                            })
                          }
                          size="sm"
                          title={`Remove tier ${index + 1}`}
                        />
                      </Flex>
                    </Flex>
                  ))}
                  <Flex>
                    <RedoButton
                      disabled={busy || current.tiers.length >= 6}
                      hierarchy="tertiary"
                      IconLeading={PlusIcon}
                      onClick={() =>
                        change({
                          tiers: [
                            ...current.tiers,
                            {
                              key: crypto.randomUUID(),
                              afterDays: "",
                              dailyRate: "",
                            },
                          ],
                        })
                      }
                      size="sm"
                      text={
                        current.tiers.length === 0
                          ? "Add first tier"
                          : "Add tier"
                      }
                    />
                  </Flex>
                  {parsed?.config && (
                    <StoragePolicySummary
                      config={parsed.config}
                      setting={setting}
                    />
                  )}
                </Flex>
              )}
              {!current.enabled && (
                <Flex
                  className={styles.policySummary}
                  dir="column"
                  gap="xs"
                  p="lg"
                >
                  <Text fontSize="xs" fontWeight="medium">
                    Storage fees are off
                  </Text>
                  <Text fontSize="xs" textColor="secondary">
                    Enable storage fees to set a free period and daily rates.
                    Previously accrued fees remain on the invoice.
                  </Text>
                </Flex>
              )}
              {parsed?.error && (
                <Text fontSize="xs" textColor="error">
                  {parsed.error}
                </Text>
              )}
              <Text fontSize="xxs" textColor="tertiary">
                Billed in {setting.currency} on the merchant's normal invoice.
                New assessments use the approved pricing. Previously assessed
                days keep their original rate.
              </Text>
              {setting.updatedAt && (
                <Text fontSize="xxs" textColor="secondary">
                  Last approved{" "}
                  {new Date(setting.updatedAt).toLocaleString(undefined, {
                    timeZone: setting.timezone,
                  })}{" "}
                  ({setting.timezone}).
                </Text>
              )}
              <Text fontSize="xxs" textColor="secondary">
                Storage age starts at an item's first bin placement, including
                receiving and staging areas. Processing stations are excluded.
                Fees apply only to individually tracked inventory currently at a
                Redo warehouse; product-only stock, lots, and items without a
                recorded bin placement are excluded.
              </Text>
              {summary && (
                <Flex dir="column" gap="xs">
                  <Text fontSize="xs">
                    Last assessment: {summary.billing_date} · {summary.quantity}{" "}
                    items · {summary.status}
                  </Text>
                  <Text fontSize="xs" textColor="secondary">
                    {summary.excluded_quantity} items excluded because bin
                    placement evidence or individual item tracking is missing.
                  </Text>
                </Flex>
              )}
            </>
          )}
        </Flex>
        {setting && (
          <Flex
            borderColor="secondary"
            borderTopWidth="1px"
            gap="sm"
            justify="flex-end"
            px="2xl"
            py="lg"
          >
            {draft && (
              <RedoButton
                disabled={busy}
                hierarchy="tertiary"
                onClick={() => setDraft(undefined)}
                size="sm"
                text="Discard"
              />
            )}
            <RedoButton
              disabled={!changed || busy}
              hierarchy="primary"
              onClick={() => void save()}
              pending={busy}
              size="sm"
              text="Save storage fees"
            />
          </Flex>
        )}
      </StepCard>
      {approval && (
        <SettingsApprovalModal
          onChoose={(mode) => void confirm(mode)}
          onClose={() => !busy && setApproval(undefined)}
          pending={busy}
          preview={approval.preview}
        />
      )}
    </>
  );
});

interface StoragePolicySummaryProps {
  config: StorageFeesConfig;
  setting: StorageFeesSetting;
}
const StoragePolicySummary = memo(function StoragePolicySummary({
  config,
  setting,
}: StoragePolicySummaryProps) {
  const freeDays = config.tiers.at(0)?.afterDays ?? 0;
  return (
    <Flex
      aria-live="polite"
      className={styles.policySummary}
      dir="column"
      gap="xs"
      p="lg"
    >
      <Text fontSize="xs" fontWeight="semibold">
        Daily fee schedule
      </Text>
      <Text fontSize="xs" textColor="secondary">
        {freeDays === 0
          ? "Fees start on day 1."
          : `First ${freeDays} days free.`}
        {config.tiers
          .map((tier, index) => {
            const next = config.tiers[index + 1];
            const range = next
              ? `Days ${tier.afterDays + 1}–${next.afterDays}`
              : `Day ${tier.afterDays + 1} onward`;
            return ` ${range}: ${setting.currency} ${tier.dailyRate} per item / day.`;
          })
          .join("")}
        {config.forgivenessPeriod &&
          ` Forgiveness applies to all stock from ${config.forgivenessPeriod.from} through ${config.forgivenessPeriod.through}.`}
      </Text>
    </Flex>
  );
});
